import { action, observable, computed } from 'mobx';
import stores from 'cd-eft/models/Stores';
import { mdiCog, mdiPencil, mdiPencilOffOutline, mdiPlusBoxOutline, mdiTrashCanOutline } from '@mdi/js';
import arrowCursorMove from 'cd-eft/assets/images/arrow-cursor-move.svg';
import { SeparatorVM } from '../../common/ContainerWithSideBar/ContainerWithSideBarVM';
import MapView from '@arcgis/core/views/MapView';
import { DynamicProfileStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileStoreBase';
import { DynamicUGOProfileStore } from 'cd-eft/models/Stores/DynamicProfile/UGO/DynamicUGOProfileStore';
import { DynamicUGOTableStore } from 'cd-eft/models/Stores/DynamicProfile/UGO/DynamicUGOTableStore';
import { formatStationString, parseStationString } from 'cd-eft/utils/stationUtilities';
import { ProfileController, UgoDesignToolsController, UgoReachController } from 'cd-eft/services/eftSetApi';
import { appConfig } from 'cd-eft/config/AppConfig';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { FixtureModalVM } from '../FixtureModal/FixtureModalVM';
import { IUgoProfileSettings, IUgoVertexParamsDTO } from 'cd-eft/models/Stores/UGODesignStore';
import { clearAllUgoEdits, saveUgoReachEdits } from 'cd-eft/components/Design/SurfaceDesigner/SurfaceDesignerVm';
import { formatRoundSTrim } from 'cd-eft/utils/mathUtilities';
import { updateStation } from 'cd-eft/models/Stores/DesignStore';

const { designStore, ugoDesignStore, surveyEngineeringStore } = stores.AppStore;

export class UGODesignVM extends SeparatorVM {
  @observable mapView?: MapView;
  @observable redigitizeReachesDialogIsOpen: boolean = false;
  protected profileStoreRef?: DynamicUGOProfileStore;
  protected tableStoreRef?: DynamicUGOTableStore;
  protected profileStoreBaseRef?: DynamicProfileStoreBase;
  private startEditing: boolean = false;
  private watchUgoEditingHandle1;
  private watchUgoEditingHandle2;
  private watchUgoEditingHandle3;
  history: any;
  @observable onClickProfileInformation = false;
  @observable checkedRowStations: any = [];
  @observable headerTitle = '';
  @observable selectedStationIdx: number | null = null;
  @observable fixtureModalVM: FixtureModalVM | null = null;
  @observable showFixtureModal = false;
  @observable loading: boolean = false;
  @observable profileParametersChanged: boolean = false;
  @observable userLeavingWarningIsShown: boolean = false;
  @observable profileSettingsForm: IUgoProfileSettings = ugoDesignStore.savedProfileSettingsForm;
  @observable savedProfileSettingsForm: IUgoProfileSettings = ugoDesignStore.savedProfileSettingsForm;
  @observable onClickInformation = false;
  @observable selectedReachId = "";
  @observable errorCode = "";
  @observable errorMessage = "";
  @observable formValidation = {
    formErrors: {
      fineGrid: '',
      lowBankOffset: '',
      sampleInterval: '',
    },
    fineGridValid: true,
    lowBankOffsetValid: true,
    sampleIntervalValid: true,
    formValid: true
  }

  @observable notification: any = {
    redigitizeReaches: '',
    userLeavingWarning: '',
  }
  constructor(props) {
    super(props);
    this.mapView = props.mapView;
    if (props.profileStoreRef) {
      this.profileStoreRef = props.profileStoreRef;
      this.profileStoreRef!.deleteAction = (index: number) => {
        this.ugoDesignStationToDelete.reachId = 'P-' + index;
        this.toggleDeleteStationDialog();
      }
    }
    if (props.tableStoreRef) {
      this.tableStoreRef = props.tableStoreRef;
      this.tableStoreRef!.setStore = surveyEngineeringStore;
      this.tableStoreRef!.ugoDesignStore = ugoDesignStore;
      this.tableStoreRef!.CurrentObject = ugoDesignStore.ugoDesignObject;
    }
    this.getErrors();
  }

  @computed get profileButtons() {
    return [
      {
        icon: mdiCog,
        title: 'Settings',
        disabled: false,
        onClick: this.openUgoSettings,
        className: 'mr-auto',
      },
      {
        icon: mdiPencil,
        title: 'Digitize Pipe',
        disabled: false,
        onClick: this.sketchUgoPipe,
      },
      {
        icon: mdiPlusBoxOutline,
        title: 'Insert Station',
        disabled: false,
        onClick: this.insertUgoVertex,
      },
      {
        img: arrowCursorMove,
        title: 'Move Station',
        disabled: false,
        onClick: this.moveVertex,
        imgClassName: 'arrowCursorMove',
      },
      {
        icon: mdiTrashCanOutline,
        title: 'Delete Station',
        disabled: false,
        onClick: this.deleteVertex,
      },
      {
        icon: mdiPlusBoxOutline,
        title: 'Add Fixture',
        disabled: false,
        onClick: this.openModalFromProfile,
      },
      {
        icon: mdiPencilOffOutline,
        title: '',
        disabled: false,
        alt: 'Stop Editing',
        onClick: () => { },
      },
    ];
  }

  get ugoTableButtons() {
    return [
      { title: 'Close', disabled: false, onClick: () => { this.handleDesignPageClose() }, className: 'usa-button--outline' },
      { title: 'Save', disabled: false, onClick: () => { this.onClickSave() }, className: 'usa-button--outline' },
      { title: 'Save & Validate', disabled: this.saveValidateButtonStatus, onClick: () => { this.onClickSaveAndValidate() } },
    ];
  }

  get saveValidateButtonStatus() {
    // This is temporary until UI Statement Management has been completed in BE
    return (
      !ugoDesignStore.ugoDesignObject.ugoDesignStations.some((station) => station.reachId !== 'P-0' && station.status === 'IN PROGRESS')
    );
  }

  get profileActions() {
    return {
      'Placeholder One': { action: () => { } },
      'Placeholder Two': { action: () => { } },
    };
  }

  ugoTableColumns = {
    defaultCols: [
      { name: 'reachId', title: 'Pipe\nSegment', editable: false },
      { name: 'startStation', title: 'Start\n(ft)', editable: false },
      { name: 'endStation', title: 'End\n(ft)', editable: false },
      { name: 'endStation', title: 'Station\n(ft)', editable: true },
      { name: 'groundElev', title: 'Ground Z\n(ft)', editable: false },
      { name: 'pipeFlowElev', title: 'Pipe Flow\nElevation (ft)', editable: true },
      { name: 'pipeFlowDepth', title: 'Pipe Flow\nDepth (ft)', editable: false },
      { name: 'pipeCoverDepth', title: 'Pipe Cover\nDepth (in)', editable: false },
      { name: 'ugoPipeSpecification.ugoMaterialDescription', title: 'Material', editable: false },
      { name: 'ugoPipeSpecification.pipelineSize', title: 'Pipe Size (in)', editable: false },
      { name: 'stationDist', title: 'Length\n(ft)', editable: false },
      { name: 'grade', title: 'Slope\n(%)', editable: false },
      { name: 'ugoPipeSpecification.pipeManningn', title: "Mannings 'n", editable: false },
      { name: 'ugoPipeSpecification.pressureFlow', title: 'Pressure\nFlow (Y/N)', editable: false },
      { name: 'runoffQ', title: 'Flow Q (cfs)', editable: false },
      { name: 'fixtureType', title: 'Fixture\nType', editable: false },
      { name: 'fixtureName', title: 'Fixture\nName', editable: false },
    ],
  };

  @action.bound async getErrors() {
    this.notification.redigitizeReaches = await MessageCenter.addErrorLabelFromCode('Profile_02');
    this.notification.userLeavingWarning = await MessageCenter.addErrorLabelFromCode('Common_15');

  }

  @observable formValidationUgoDesign = { ...initialFormValidationUgoDesign };

  @action.bound setValue = (col, rowData) => {
    const isEditable = col.editable;
    let value;
    if (col.name.includes('.')) {
      const [parentKey, childKey] = col.name.split('.');
      value = rowData[parentKey]?.[childKey] ?? '';
    } else {
      value = rowData[col.name];
    }
    // if (col.name === 'grade') {
    //   value = (value * 100).toFixed(2);
    // }
    if (col.name === 'fixtureType') {
      value = ugoDesignStore.fixtureTypes.get(rowData['fixtureTypeId'])?.ugoFixtureTypeName;
    }
    if (col.name === 'ugoPipeSpecification.pressureFlow') {
      value = value ? 'Yes' : 'No';
    }
    if (col.name === 'isPressureFlow') {
      value = value ? 'Yes' : 'No';
    }

    // Decimal format the value based on the column name
    if (!isEditable) {
      if (['groundElev', 'stationDist'].includes(col.name)) {
        value =
          value !== null && value !== undefined && value !== ''
            ? formatRoundSTrim(parseFloat(value), 1)
            : value;
      } else if (col.name === 'grade') {
        value = (value * 100).toFixed(2)
      } else if (typeof value === 'number') {
        value = formatRoundSTrim(value, 2);
      }
    } else {
      if (value === null || value === undefined) {
        value = '';
      } else if (typeof value === 'number') {
        if (col.name === 'stationDist') {
          value = parseFloat(value.toFixed(1)).toString();
        } else {
          value = value.toString();
        }
      }
    }

    return [isEditable, value];
  };

  @action.bound async loadUgoDataAndProfiles() {
    designStore.designElementTypeId = appConfig.hardCodes.designElementTypeUGO; //To enable the PEM Toolbar in the map view during edit UGO.

    if (ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId) {
      await this.init();
      await this.profileStoreRef?.generateUgoProfiles(Number(this.profileSettingsForm.input.sampleInterval));
      await this.tableStoreRef?.addRows(ugoDesignStore.ugoDesignObject.ugoDesignStations);

      let gridValue: number = 0;
      if (this.profileSettingsForm.checked.showFineGrid) {
        gridValue = Number(this.profileSettingsForm.input.fineGrid);
        this.profileStoreRef!.updateUserGridValues(gridValue);
      } else {
        this.profileStoreRef!.updateUserGridValues(0);
      }
    }
  }

  @action.bound async init() {
    await getUgoVertexParamsOnDesign();
  }

  @action.bound onClickProfileInformationToggle = (title) => {
    this.headerTitle = title;
    this.onClickProfileInformation = !this.onClickProfileInformation;
  };

  protected async handleMouseUpResizeActions(currentViewExtent) {
    if (this.profileStoreRef?._profileView && currentViewExtent) {
      this.profileStoreRef._profileView.extent = currentViewExtent;
      await this.profileStoreRef.redrawUgoProfiles();
    }
  }

  @action.bound sketchUgoPipe(e: any) {
    e.preventDefault();
    if (this.profileStoreRef?.checkSketch()) {
      this.toggleRedigitizeUgoPipeDialog();
    } else {
      this.digitizeUgoPipe();
    }
  }

  @action.bound toggleRedigitizeUgoPipeDialog() {
    this.redigitizeReachesDialogIsOpen = !this.redigitizeReachesDialogIsOpen;
  }

  @action.bound async digitizeUgoPipe() {
    if (this.redigitizeReachesDialogIsOpen) this.toggleRedigitizeUgoPipeDialog();
    this.profileStoreRef?.sketchPolyline();
  }

  @action.bound moveVertex(e: any) {
    e.preventDefault();
    this.startUgoEditingMode();
    this.profileStoreRef?.reshapePolyline(this.props.addUgoEdits);
  }

  @action.bound deleteVertex(e: any) {
    e.preventDefault();
    this.startUgoEditingMode();
    this.profileStoreRef?.deletePoint();
  }

  @action.bound insertUgoVertex(e: any) {
    e.preventDefault();
    this.startUgoEditingMode();
    this.profileStoreRef?.SketchPoint(this.props.addUgoEdits);
  }

  @action.bound stopUgoEditingMode() {
    this.profileStoreRef?.stopEditing();
    this.watchUgoEditingHandle1?.remove();
    this.watchUgoEditingHandle2?.remove();
    this.watchUgoEditingHandle3?.remove();
    this.startEditing = false;
  }

  private handleUgoEditingClick() {
    if (this.startEditing) {
      this.stopUgoEditingMode();
    }
  }

  private startUgoEditingMode() {
    this.startEditing = true;
    const profileBtns = document.getElementById('UGO-design-profile-btns');
    const header = document.getElementsByClassName('profile-reaches-info');
    const btns = document.getElementById('ugo-design-btns');
    this.watchUgoEditingHandle1 = profileBtns?.addEventListener('click', this.handleUgoEditingClick.bind(this));
    this.watchUgoEditingHandle2 = header[0]?.addEventListener('click', this.handleUgoEditingClick.bind(this));
    this.watchUgoEditingHandle3 = btns?.addEventListener('click', this.handleUgoEditingClick.bind(this));
  }

  @action.bound selectAll() { }

  @action.bound async insertStationUgoTableView(e: any, item: any) {
    const stations1 = ugoDesignStore.ugoDesignObject.ugoDesignStations;
    const stations = JSON.parse(JSON.stringify(stations1));
    const originalStations = JSON.parse(JSON.stringify(stations));

    const index = stations.findIndex((station) => station.reachId === item.reachId);
    // Create a deep copy of the item
    const newItem = JSON.parse(JSON.stringify(item));
    newItem.endStation = stations[index].endStation;

    if (index > 0) {
      // Remove '+' sign from endStation and startStation
      const startStation = parseFloat(stations[index].startStation?.replace('+', '') ?? '0');
      const endStation = parseFloat(stations[index].endStation?.replace('+', '') ?? '0');
      stations[index].endStation = formatStationString(((startStation + endStation) / 2.0).toFixed(1).toString());
      newItem.startStation = stations[index].endStation;

      // Calculate the average point of GroundZ and the GroundZ of the station above it
      const currentGroundZ = parseFloat(stations[index].groundElev.toString());
      const currenPipeFlowZ = parseFloat(stations[index].pipeFlowElev.toString());
      const prevGroundZ = parseFloat(stations[index - 1].groundElev.toString());
      const prevPipeFlowZ = parseFloat(stations[index - 1].pipeFlowElev.toString());
      stations[index].groundElev = parseFloat(((currentGroundZ + prevGroundZ) / 2).toFixed(1));
      stations[index].pipeFlowElev = parseFloat(((currenPipeFlowZ + prevPipeFlowZ) / 2).toFixed(1));
      // Calculate stationLength and grade for the current index
      const prevStation = stations[index - 1];
      if (prevStation) {
        this.recalculateSlopeAndLength(stations[index], prevStation);
      }
    }
    const payload = {
      alignedElementDesignParametersId: ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId,
      reachStationDistance:
        parseStationString(newItem.endStation?.toString() ?? '0') -
        parseStationString(stations[1].startStation?.toString() ?? '0'),
    };
    const response = await ProfileController.getGroundElevationByReachStationDistanceFromAlignmentUsingPOST(payload);
    if (response.data && response.data.groundElevation !== undefined) {
      newItem.groundElev = parseFloat(response.data.groundElevation.toFixed(1));
    }

    //Calculate Station Length and grade for the new station item
    const prevStation = stations[index];
    this.recalculateSlopeAndLength(newItem, prevStation);
    prevStation.status = 'IN PROGRESS';

    // Insert the new item at the next index
    stations.splice(index + 1, 0, newItem);

    // Create the array to hold the updated stations
    const stationsInsertedAndUpdated: any = [];

    // Add the current index item to the array
    stationsInsertedAndUpdated.push(stations[index]);

    // Update the reachId of the new item and all subsequent items
    for (let i = index + 1; i < stations.length; i++) {
      if (stations[i].reachId !== 'P-0') {
        const reachIdNumber = parseInt(stations[i].reachId.split('-')[1]);
        stations[i].reachId = `P-${reachIdNumber + 1}`;
        stations[i].status = 'IN PROGRESS';
        stationsInsertedAndUpdated.push(stations[i]);
      }
    }

    // Create a new Map to hold all the updated values
    const allUpdatedValues = new Map();
    // Compare original and updated stations to find the stations that were added or modified
    stationsInsertedAndUpdated.forEach((updatedStation) => {
      const originalStation = originalStations.find((os) => {
        if (updatedStation.originalReachId) {
          return os.reachId === updatedStation.originalReachId;
        }
        return os.endStation === updatedStation.endStation;
      });

      if (!originalStation) {
        // A newly added station
        allUpdatedValues.set('Add', updatedStation);
      } else {
        const isChanged =
          originalStation.endStation !== updatedStation.endStation ||
          originalStation.reachId !== updatedStation.reachId ||
          parseFloat(originalStation.groundElev) !== parseFloat(updatedStation.groundElev);

        if (isChanged) {
          allUpdatedValues.set(originalStation.reachId, updatedStation);
        }
      }
    });

    ugoDesignStore.ugoDesignObject.ugoDesignStations = stations;
    await this.props.addUgoEdits(allUpdatedValues, true);
    ugoDesignStore.ugoInputsChanged = true;
    await this.profileStoreRef?.createChannelFromSavedUgo();
  }

  // delete ugoDesign station -->
  @observable deleteStationDialogIsOpen: boolean = false;
  @observable ugoDesignStationToDelete: any = {};

  // Delete Station from Table view
  @action.bound async deleteStationTableView(e: any, item: any) {
    e.preventDefault();
    this.ugoDesignStationToDelete.reachId = item.reachId ? item.reachId : 'P-0';
    ugoDesignStore.deleteStationFromTable = true;
    this.toggleDeleteStationDialog();
  }

  @action.bound toggleDeleteStationDialog() {
    this.deleteStationDialogIsOpen = !this.deleteStationDialogIsOpen;
  }

  @action.bound async deleteUgoDesignStation() {
    const stations = ugoDesignStore.ugoDesignObject.ugoDesignStations;

    if (this.ugoDesignStationToDelete.reachId.substring(0, 1) === 'P' && stations.length > 3) {
      const indexToDelete = stations.findIndex((s) => s.reachId === this.ugoDesignStationToDelete.reachId);
      const editedUgoDesignData = new Map();
      const deleteStation = stations[indexToDelete];
      deleteStation.reachId = 'Delete';
      editedUgoDesignData.set(this.ugoDesignStationToDelete.reachId, deleteStation);

      stations.splice(indexToDelete, 1);

      // Update remaining stations
      for (let i = indexToDelete; i < stations.length; i++) {
        const originalReachId = stations[i].reachId;
        stations[i].reachId = 'P-' + (Number(stations[i].reachId.substring(2)) - 1);

        if (i === indexToDelete) {
          // Update first station after deletion
          stations[i].startStation = stations[i - 1].endStation;
          this.recalculateSlopeAndLength(stations[i], stations[i - 1]);
        }

        stations[i].status = 'IN PROGRESS';
        editedUgoDesignData.set(originalReachId, stations[i]);
      }

      await this.props.addUgoEdits(editedUgoDesignData, true);
      ugoDesignStore.ugoInputsChanged = true;
      ugoDesignStore.ugoDesignObject.ugoDesignStations = JSON.parse(JSON.stringify(stations));
      await this.profileStoreRef?.deleteReachVertex({ index: indexToDelete });

      this.ugoDesignStationToDelete.reachId = '';
      this.toggleDeleteStationDialog();

      if (ugoDesignStore.deleteStationFromTable) {
        ugoDesignStore.deleteStationFromTable = false;
        await this.profileStoreBaseRef?.createChannelFromSavedUgo();
      } else {
        this.profileStoreBaseRef?.addCenterReachLabels(stations);
      }
      this.profileStoreRef?.stopEditing();
    } else {
      this.ugoDesignStationToDelete.reachId = '';
      this.toggleDeleteStationDialog();
    }
  }

  @action.bound handleRowMouseEnter(e: any, i: number) {
    // this.trackTableRow(i, true);
  }
  @action.bound handleRowMouseLeave(e: any, i: number) {
    // this.trackTableRow(i, false);
  }

  @action.bound onClickInformationToggle(reachId) {
    const station = ugoDesignStore.ugoDesignObject.ugoDesignStations.find(r => r.reachId === reachId);
    this.selectedReachId = reachId
    if (station?.errorResponse) {
      this.errorCode = station.errorResponse.errorCode || "Test";
      this.errorMessage = station.errorResponse.errorMessage || "Test";
    }
    this.onClickInformation = !this.onClickInformation;
  }

  @observable changedInputValue: boolean = false;

  @action.bound handleInputChange(event: any, index: number, fieldName: string) {
    const { value } = event.target;
    const stations = ugoDesignStore.ugoDesignObject.ugoDesignStations;
    ugoDesignStore.ugoInputsChanged = true;
    let prevValue: any;
    if (fieldName.includes('.')) {
      const [parentKey, childKey] = fieldName.split('.');
      if (!stations[index][parentKey]) {
        stations[index][parentKey] = {};
      }
      prevValue = stations[index][parentKey][childKey];
      stations[index][parentKey][childKey] = value;
    } else {
      prevValue = stations[index][fieldName];
      stations[index][fieldName] = value;
    }
    if (String(prevValue) !== String(value)) {
      this.changedInputValue = false;
      stations[index].status = 'IN PROGRESS';
      this.changedInputValue = true;
    } else {
      this.changedInputValue = false;
    }
    // this.validateFieldReachTable(fieldName, value, index);
  }

  @action.bound async handleTableInputChange(event: any, index: number, fieldName: string) {
    if (!this.changedInputValue) {
      return;
    } else {
      const stations = ugoDesignStore.ugoDesignObject.ugoDesignStations;
      // Set inputs changed flag when table input changes are processed
      ugoDesignStore.ugoInputsChanged = true;
      await this.editStationUgoTableView(event, stations[index], fieldName);
    }
  }

  @action.bound async editStationUgoTableView(e: any, item: any, name: string) {
    const stations = ugoDesignStore.ugoDesignObject.ugoDesignStations;
    const index = stations.findIndex((station) => station.reachId === item.reachId);
    if (index === -1) return;

    updateStation(item, stations, index);

    // Handle each field type separately
    const editedStationObject = new Map();

    if (name === 'endStation') {
      const currentStation = stations[index].endStation;
      let editedValue;

      // Handle both input formats
      if (currentStation.indexOf("+") !== -1) {
        editedValue = formatStationString(parseStationString(currentStation).toString());
      } else {
        editedValue = formatStationString(parseFloat(currentStation).toFixed(1));
      }

      // Update current station
      stations[index].endStation = editedValue;
      // Update next station's start value
      if (index + 1 < stations.length) {
        stations[index + 1].startStation = stations[index].endStation;
      }

      // Ground elevation recalculation
      const response = await ProfileController.getGroundElevationByReachStationDistanceFromAlignmentUsingPOST({
        alignedElementDesignParametersId: ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId,
        reachStationDistance: parseStationString(editedValue) - parseStationString(stations[1].startStation),
      });

      if (response.data?.groundElevation !== undefined) {
        stations[index].groundElev = parseFloat(response.data.groundElevation.toFixed(1));
      }
    }

    if (name === 'pipeFlowElev') {
      const currentElevation = stations[index].pipeFlowElev as any;
      const editedValue = parseFloat(currentElevation).toFixed(1);
      stations[index].pipeFlowElev = parseFloat(editedValue);
    }

    // Set edits
    this.recalculateSlopeAndLength(stations, index);
    stations[index].status = 'IN PROGRESS';
    editedStationObject.set(stations[index].reachId, stations[index]);
    if ((name === 'endStation' || name === 'pipeFlowElev') && index + 1 < stations.length) {
      this.recalculateSlopeAndLength(stations, index + 1);
      stations[index + 1].status = 'IN PROGRESS';
      editedStationObject.set(stations[index + 1].reachId, stations[index + 1]);
    }

    // await this.props.addUgoEdits(editedStationObject, false, true); // with 'false' PEM doesn't count change
    await this.props.addUgoEdits(editedStationObject, true);
    ugoDesignStore.ugoInputsChanged = true;
    this.changedInputValue = false;
    this.profileStoreRef?.createChannelFromSavedUgo();
  }


  // Helper method to recalculate slope and length
  recalculateSlopeAndLength(currentStation, prevStation) {
    if (currentStation && prevStation) {
      const deltaY = prevStation.pipeFlowElev - currentStation.pipeFlowElev;
      const prevEndStation = parseStationString(prevStation.endStation ?? '');
      const currentEndStation = parseStationString(currentStation.endStation ?? '');
      const deltaX = prevEndStation - currentEndStation;
      currentStation.grade = deltaX !== 0 ? deltaY / deltaX : 0;
      currentStation.stationDist = Math.abs(parseFloat(deltaX.toFixed(1)));
    }
  }

  @action.bound openModalFromProfile() {
    // this.profileStoreRef?.unTrackUnselectedProfilePoints();
    this.startUgoEditingMode();
    const modalFunction = this.openFixtureModal;
    this.profileStoreRef?.startProfileEditTool(modalFunction);
  }

  @action.bound openFixtureModal(e: any, item: IUgoVertexParamsDTO, i: number) {
    const hasFixture = !!item.fixtureTypeId;
    const mode = hasFixture ? 'edit' : 'add';
    this.selectedStationIdx = i;
    this.fixtureModalVM = new FixtureModalVM(JSON.parse(JSON.stringify(item)), mode);
    this.showFixtureModal = true;
  }

  @action.bound closeFixtureModal() {
    this.showFixtureModal = false;
    this.fixtureModalVM = null;
    this.selectedStationIdx = null;
  }

  @action.bound applyFixtureUpdates = async (updatedStation: IUgoVertexParamsDTO) => {
    await this.applyStationUpdates(updatedStation);
    this.closeFixtureModal();
  }

  @action.bound async applyStationUpdates(updatedStation: IUgoVertexParamsDTO) {
    if (this.selectedStationIdx !== null) {
      const stations = ugoDesignStore.ugoDesignObject.ugoDesignStations;
      const originalStation = stations[this.selectedStationIdx];
      const editedUgoDesignData = new Map();
      // Update the station in the stations array
      stations[this.selectedStationIdx] = {
        ...originalStation,
        ...updatedStation,
        status: 'IN PROGRESS'
      };
      editedUgoDesignData.set(originalStation.reachId, stations[this.selectedStationIdx]);
      await this.props.addUgoEdits(editedUgoDesignData, true);
      ugoDesignStore.ugoInputsChanged = true;

      ugoDesignStore.ugoDesignObject.ugoDesignStations = stations;
      await this.profileStoreRef?.createChannelFromSavedUgo();
    } else {
      MessageCenter.addError('Unable to apply fixture changes.');
    }
  }

  @action.bound setElementActions(item: IUgoVertexParamsDTO) {
    const hasFixture = !!item.fixtureTypeId;
    return {
      'Insert Station': { action: this.insertStationUgoTableView, disabled: item.reachId === 'P-0' },
      'Delete Station': { action: this.deleteStationTableView, disabled: ugoDesignStore.ugoDesignObject.ugoDesignStations.length < 4 },
      [hasFixture ? 'Edit Fixture' : 'Add Fixture']: { action: this.openFixtureModal },
      'Delete Fixture': { action: this.deleteFixture, disabled: !hasFixture },
      'Edit Pipe': { action: this.editPipe, disabled: false },
    };
  }

  @action.bound deleteFixture = (e, item: IUgoVertexParamsDTO, i: number) => {
    this.selectedStationIdx = i;
    const updatedItem: IUgoVertexParamsDTO = {
      ...item,
      fixtureDtoJson: null,
      fixtureName: null,
      fixtureTypeId: 0
    }
    this.applyStationUpdates(updatedItem);
    this.selectedStationIdx = null;
  }

  @action.bound async onClickSave() {
    await this.saveUgoDesign('save');
  }

  @action.bound async onClickSaveAndValidate() {
    await this.saveUgoDesign('saveAndValidate');
  }

  @action.bound async saveUgoDesign(savingType: 'save' | 'saveAndValidate') {
    // call saveUgoReachEdits() function in SurfaceDesignerVM --> call API UgoReachController.saveUgoReachEditsUsingPOST
    try {
      this.loading = true;
      if (savingType === 'save') {
        designStore.setLoader('Saving Pending Edits...', "Saving", 0, 100);
        if (designStore.ugoGroupsLength > 0) {
          const isSuccess = await saveUgoReachEdits();
          if (isSuccess) {
            ugoDesignStore.ugoInputsChanged = false;
          }
        }

      }
      else if (savingType === 'saveAndValidate') {
        designStore.setLoader('Saving Pending Edits...', "Save & Calculate", 0, 15);
        if (designStore.ugoGroupsLength > 0) {
          const isSuccess = await saveUgoReachEdits();
          if (isSuccess) {
            ugoDesignStore.ugoInputsChanged = false;
          }
        }
        try {
          designStore.setLoader('Pipe Simulation And Validation...', "Save & Calculate", 16, 90);
          const pipeSimulationResp = await UgoDesignToolsController.pipeSimulationAndValidationUsingPOST(designStore.projectsStore.currentSessionId, ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId)
          if (pipeSimulationResp && pipeSimulationResp.data) {
            const pipeSimulationRespData = pipeSimulationResp.data

            MessageCenter.clearMessages();
            if (pipeSimulationRespData.errorResponse && pipeSimulationRespData.errorResponse.errorType === 'ERROR' && pipeSimulationRespData.errorResponse.errorCode && pipeSimulationRespData.errorResponse.errorMessage) {
              MessageCenter.addNotification(pipeSimulationRespData.errorResponse.errorMessage, pipeSimulationRespData.errorResponse.errorType, pipeSimulationRespData.errorResponse.errorCode);
            }
            else if (!pipeSimulationRespData.errorResponse || (pipeSimulationRespData.errorResponse && pipeSimulationRespData.errorResponse.errorType !== 'ERROR' && pipeSimulationRespData.errorResponse.errorCode && pipeSimulationRespData.errorResponse.errorMessage)) {
              //show if pipeSimulationRespData has one or more information messages
              if ((pipeSimulationRespData.errorResponse && pipeSimulationRespData.errorResponse.errorType === 'INFORMATION' && pipeSimulationRespData.errorResponse.errorCode && pipeSimulationRespData.errorResponse.errorMessage)) {
                MessageCenter.addNotification(pipeSimulationRespData.errorResponse.errorMessage, pipeSimulationRespData.errorResponse.errorType, pipeSimulationRespData.errorResponse.errorCode);
              }
              //show if pipeSimulationRespData has one or more warning messages
              if ((pipeSimulationRespData.errorResponse && pipeSimulationRespData.errorResponse.errorType === 'WARNING' && pipeSimulationRespData.errorResponse.errorCode && pipeSimulationRespData.errorResponse.errorMessage)) {
                MessageCenter.addNotification(pipeSimulationRespData.errorResponse.errorMessage, pipeSimulationRespData.errorResponse.errorType, pipeSimulationRespData.errorResponse.errorCode);
              }
            }
            //reload table view
            designStore.setLoader('Pipe Simulation And Validation finalizing, loading...', "Save & Validate", 91, 100);
            await getUgoVertexParamsOnDesign();
            ugoDesignStore.ugoInputsChanged = false;
          }
        } catch (error) {
          MessageCenter.addError((error as Error).message);
        }
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      designStore.setLoader('');
      designStore.setLoader('');
      designStore.setLoader('');
      this.loading = false;
    }
  }

  @action.bound openUgoSettings() {
    MessageCenter.clearMessages();
    this.profileSettingsForm = JSON.parse(JSON.stringify(this.savedProfileSettingsForm));
    this.profileSettingsForm.profileSettingPopUpActive = true;
  }

  @action.bound toggleFineGrid() {
    this.profileSettingsForm.checked.showFineGrid = !this.profileSettingsForm.checked.showFineGrid;
  }

  @action.bound handleCancelProfileSettings() {
    this.profileSettingsForm = JSON.parse(JSON.stringify(this.savedProfileSettingsForm));
    this.profileSettingsForm.profileSettingPopUpActive = false;
  }

  @action.bound async handleSaveProfileSettings() {
    if (this.profileSettingsForm.input.sampleInterval !== this.savedProfileSettingsForm.input.sampleInterval
      || this.profileSettingsForm.checked.showFineGrid !== this.savedProfileSettingsForm.checked.showFineGrid
      || this.profileSettingsForm.input.fineGrid !== this.savedProfileSettingsForm.input.fineGrid) {
      this.profileParametersChanged = true;
    }
    this.profileSettingsForm.profileSettingPopUpActive = false;
    this.savedProfileSettingsForm = JSON.parse(JSON.stringify(this.profileSettingsForm));
    ugoDesignStore.savedProfileSettingsForm = this.savedProfileSettingsForm;

    if (this.profileParametersChanged) {
      await this.profileStoreRef?.generateUgoProfiles(Number(this.profileSettingsForm.input.sampleInterval));
    }

    let gridValue: number = 0;
    if (this.profileSettingsForm.checked.showFineGrid) {
      gridValue = Number(this.profileSettingsForm.input.fineGrid);
      this.profileStoreRef!.updateUserGridValues(gridValue);
    } else {
      this.profileStoreRef!.updateUserGridValues(0);
    }
  }

  @observable showEditPipeModal: boolean = false;
  @action.bound toggleEditPipeModal = () => {
    this.showEditPipeModal = !this.showEditPipeModal;
  }

  @action.bound editPipe = (e, item: IUgoVertexParamsDTO, i) => {
    if (item) {
      ugoDesignStore.inputForms.PipelineDefaultSection.material = item.ugoPipeSpecification.materialTypeId!;
      ugoDesignStore.inputForms.PipelineDefaultSection.pipeSize = item.ugoPipeSpecification.pipelineSize;
      ugoDesignStore.inputForms.PipelineDefaultSection.pressureFlow = item.ugoPipeSpecification.pressureFlow;
      ugoDesignStore.inputForms.PipelineDefaultSection.pressurizable = item.ugoPipeSpecification.pressurizable!;
      this.selectedStationIdx = i;
      this.toggleEditPipeModal();
    }
  }

  @action.bound applyPipeUpdates = () => {
    const updatedPipe = ugoDesignStore.ugoDesignObject.ugoDesignStations[this.selectedStationIdx!]
    const selectedMaterial = ugoDesignStore.pipelineMaterials.get(ugoDesignStore.inputForms.PipelineDefaultSection.material);
    updatedPipe.ugoPipeSpecification.materialTypeId = ugoDesignStore.inputForms.PipelineDefaultSection.material!;
    updatedPipe.ugoPipeSpecification.ugoMaterialDescription = selectedMaterial!.pipelineMaterialName!;
    updatedPipe.ugoPipeSpecification.pipelineSize = ugoDesignStore.inputForms.PipelineDefaultSection.pipeSize;
    updatedPipe.ugoPipeSpecification.pressureFlow = ugoDesignStore.inputForms.PipelineDefaultSection.pressureFlow;
    updatedPipe.ugoPipeSpecification.pressurizable = ugoDesignStore.inputForms.PipelineDefaultSection.pressurizable!;
    this.applyStationUpdates(updatedPipe);
    this.showEditPipeModal = !this.showEditPipeModal;
  };
  @action.bound userLeavingWarningOnOk = () => {
    this.userLeavingWarningIsShown = !this.userLeavingWarningIsShown;
  }

  @action.bound handleDesignPageClose() {
    if (designStore.ugoGroupsLength > 0) {
      MessageCenter.addWarning(ugoDesignStore.notifications?.unsavedEditsClosePage, [
        {
          callback: () => {
            MessageCenter.clearMessages();
          },
          text: 'Cancel',
        },
        {
          callback: () => {
            MessageCenter.clearMessages();
            ugoDesignStore.reset();
            clearAllUgoEdits();
            setTimeout(() => {
              ugoDesignStore.closeAction();
            }, 0);
          },
          text: 'OK',
        },
      ], 'Common_13');
    } else {
      MessageCenter.clearMessages();
      ugoDesignStore.reset();
      ugoDesignStore.closeAction();
    }
  }
}

export const getUgoVertexParamsOnDesign = async () => {
  try {
    const response = await UgoReachController.getUgoVertexParamsDTOsByAlignedElementIdUsingGET(ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId);
    if (response?.data && response.data.length) {
      ugoDesignStore.ugoDesignObject.ugoDesignStations = response.data.map(item => ({
        ...item,
        pipeFlowElev: item.pipeFlowElev ? Math.round(item.pipeFlowElev * 10) / 10 : 0,
        grade: item.grade ? item.grade : 0,
        errorResponse: {
          ...item.errorResponse
        },
      })) as IUgoVertexParamsDTO[];

    }
  }
  catch (error) {
    console.error(error)
  }
}

export const initialFormValidationUgoDesign = {
  formErrors: {
    endStation: '',
    groundElev: '',
    stationDist: '',
    grade: '',
  },
  endStationValid: true,
  groundElevValid: true,
  stationDistValid: true,
  gradeValid: true,
  formValid: true,
  invalidFields: [] as { fieldName: string; index: number }[],
};
